import { z } from "zod";
import {
  recordingIdSchema
} from "../entities/recording.js";
import {
  normalizeForTextSearch,
  splitsWord
} from "./textSearch.js";
const transcriptSnippetLocationSchema = z.object({
  recordingId: recordingIdSchema,
  startFragmentIndex: z.number().int().nonnegative(),
  endFragmentIndex: z.number().int().nonnegative(),
  startCharOffset: z.number().nonnegative().optional(),
  endCharOffset: z.number().nonnegative().optional()
}).refine(
  (location) => location.startFragmentIndex <= location.endFragmentIndex,
  {
    message: "startFragmentIndex must not exceed endFragmentIndex"
  }
);
const transcriptSnippetParamSchema = z.object({
  /** Payload version. */
  v: z.literal(1),
  /** Recording ID. */
  rId: recordingIdSchema,
  /** Start fragment index. */
  sf: z.number().int().nonnegative(),
  /** End fragment index; defaults to `sf`. */
  ef: z.number().int().nonnegative().optional(),
  /** Character offset within the start fragment. */
  so: z.number().nonnegative().optional(),
  /** Character offset within the end fragment. */
  eo: z.number().nonnegative().optional()
});
function buildTranscriptSnippetParam(location) {
  const payload = {
    v: 1,
    rId: location.recordingId,
    sf: location.startFragmentIndex,
    ef: location.endFragmentIndex,
    ...location.startCharOffset !== void 0 ? { so: location.startCharOffset } : {},
    ...location.endCharOffset !== void 0 ? { eo: location.endCharOffset } : {}
  };
  return JSON.stringify(payload);
}
function parseTranscriptSnippetParam(raw) {
  let payload;
  try {
    payload = transcriptSnippetParamSchema.parse(JSON.parse(raw));
  } catch {
    return void 0;
  }
  const parsed = transcriptSnippetLocationSchema.safeParse({
    recordingId: payload.rId,
    startFragmentIndex: payload.sf,
    endFragmentIndex: payload.ef ?? payload.sf,
    startCharOffset: payload.so,
    endCharOffset: payload.eo
  });
  return parsed.success ? parsed.data : void 0;
}
function encodeTranscriptSnippetMarker(location) {
  return btoa(buildTranscriptSnippetParam(location));
}
function decodeTranscriptSnippetMarker(raw) {
  let wire;
  try {
    wire = atob(raw);
  } catch {
    return void 0;
  }
  return parseTranscriptSnippetParam(wire);
}
function checkTranscriptSnippetLocationWithinBounds(location, fragments) {
  const start = fragments[location.startFragmentIndex];
  const end = fragments[location.endFragmentIndex];
  if (!start || !end) {
    return false;
  }
  if (location.startFragmentIndex === location.endFragmentIndex && location.startCharOffset !== void 0 && location.endCharOffset !== void 0 && location.startCharOffset > location.endCharOffset) {
    return false;
  }
  if (location.startCharOffset !== void 0 && location.startCharOffset > start.text_transcript.length) {
    return false;
  }
  return location.endCharOffset === void 0 || location.endCharOffset <= end.text_transcript.length;
}
function prepareTranscriptForSnippetSearch(recording) {
  const slices = [];
  let joined = "";
  recording.fragments.forEach((fragment, fragmentIndex) => {
    const normalized = normalizeForTextSearch(fragment.text_transcript);
    if (!normalized.text) {
      return;
    }
    if (joined) {
      joined += " ";
    }
    slices.push({ fragmentIndex, joinedStart: joined.length, normalized });
    joined += normalized.text;
  });
  return { id: recording.id, fragments: recording.fragments, joined, slices };
}
function locateSnippetForQuote(quoteText, recordings, preferredWindows) {
  const needle = normalizeForTextSearch(quoteText).text;
  if (!needle) {
    return void 0;
  }
  const matches = [];
  for (const recording of recordings) {
    const prepared = "slices" in recording ? recording : prepareTranscriptForSnippetSearch(recording);
    matches.push(...findQuoteMatchesInTranscript(needle, prepared));
  }
  if (matches.length === 1) {
    return matches[0];
  }
  const inWindows = preferredWindows?.length ? matches.filter(
    (match) => checkMatchInWindows(match, recordings, preferredWindows)
  ) : [];
  return inWindows.length === 1 ? inWindows[0] : void 0;
}
function checkMatchInWindows(match, recordings, windows) {
  const fragments = recordings.find(
    (r) => r.id === match.recordingId
  )?.fragments;
  const startFragment = fragments?.[match.startFragmentIndex];
  if (!startFragment) {
    return false;
  }
  return windows.some(
    (window) => window.recordingId === match.recordingId && startFragment.end_time >= window.startTimeMs && startFragment.start_time <= window.endTimeMs
  );
}
function findQuoteMatchesInTranscript(needle, recording) {
  const { joined, slices } = recording;
  const matches = [];
  for (let matchStart = joined.indexOf(needle); matchStart !== -1; matchStart = joined.indexOf(needle, matchStart + 1)) {
    const matchEnd = matchStart + needle.length;
    if (splitsWord(joined, matchStart) || splitsWord(joined, matchEnd)) {
      continue;
    }
    const start = resolveJoinedIndex(slices, matchStart);
    const endInclusive = resolveJoinedIndex(slices, matchEnd - 1);
    if (!start || !endInclusive) {
      continue;
    }
    matches.push({
      recordingId: recording.id,
      startFragmentIndex: start.fragmentIndex,
      endFragmentIndex: endInclusive.fragmentIndex,
      startCharOffset: start.charOffset,
      endCharOffset: endInclusive.charEndOffset
    });
  }
  return matches;
}
function resolveJoinedIndex(slices, joinedIndex) {
  for (let index = slices.length - 1; index >= 0; index--) {
    const slice = slices[index];
    if (joinedIndex < slice.joinedStart) {
      continue;
    }
    const offsetInSlice = joinedIndex - slice.joinedStart;
    if (offsetInSlice >= slice.normalized.text.length) {
      return void 0;
    }
    return {
      fragmentIndex: slice.fragmentIndex,
      charOffset: slice.normalized.sourceIndexes[offsetInSlice],
      charEndOffset: slice.normalized.sourceEndIndexes[offsetInSlice]
    };
  }
  return void 0;
}
export {
  buildTranscriptSnippetParam,
  checkTranscriptSnippetLocationWithinBounds,
  decodeTranscriptSnippetMarker,
  encodeTranscriptSnippetMarker,
  locateSnippetForQuote,
  parseTranscriptSnippetParam,
  prepareTranscriptForSnippetSearch,
  transcriptSnippetLocationSchema,
  transcriptSnippetParamSchema
};
//# sourceMappingURL=transcriptSnippetLocation.js.map
